import moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { ACTOR_PHRASES } from '../metadata/actor_sentence';
import AgentMetadata from '../metadata/agent_metadata';
import ApiMetadata from '../metadata/api_metadata';
import Communicator from 'models/communicator';
import connect from 'components/lib/connect';
import Conversation from 'models/conversation';
import ConversationItem from 'models/conversation_item';
import CustomerMetadata from '../metadata/customer_metadata';
import ItemContent from '../item_content';
import Metadata from '../metadata/metadata';
import PrepositionalPhrase from '../metadata/prepositional_phrase';
import RuleLink from '../metadata/rule_link';

export function StatusChangeItem({ item }) {
  const { t } = useTranslation();
  let content = renderContent();
  if (!content) {
    return null;
  }

  return (
    <ItemContent isAuditItem item={item} showIcon={false}>
      {content}
    </ItemContent>
  );

  function renderContent() {
    switch (item.initiator.type) {
      case Communicator.AGENT:
        return getAgentStatusChange();
      case Communicator.API:
        return getApiStatusChange();
      case Communicator.AUTOMATED:
        return item.initiator.id ? getRuleStatusChange() : getAgentStatusChange();
      case Communicator.CUSTOMER:
        if (item.content.status === Conversation.Status.CLOSED) {
          return null;
        }
        return getReopenedByCustomer();
      case Communicator.DEFAULT:
        if (item.content.status !== Conversation.Status.CLOSED) {
          return null;
        }
        return (
          <Metadata isInbound={false} text={t('Conversation was closed automatically')} timestamp={item.timestamp} />
        );
      default:
        return getAgentStatusChange();
    }
  }

  function getAgentStatusChange() {
    return (
      <AgentMetadata
        item={item}
        phrase={
          item.content.status === Conversation.Status.CLOSED
            ? ACTOR_PHRASES.CLOSED_CONVERSATION
            : ACTOR_PHRASES.OPENED_CONVERSATION
        }
      />
    );
  }

  function getApiStatusChange() {
    return (
      <ApiMetadata
        item={item}
        text={item.content.status === Conversation.Status.CLOSED ? t('Conversation closed') : t('Conversation opened')}
      />
    );
  }

  function getReopenedByCustomer() {
    let reopenWindow = moment.duration(
      item.content.reopenWindow ? `PT${item.content.reopenWindow.toUpperCase()}` : NaN
    );

    return (
      <CustomerMetadata
        item={item}
        text={
          reopenWindow.asSeconds() > 0
            ? t('opened this conversation by responding within {{window}}', { window: reopenWindow.humanize() })
            : t('opened this conversation by responding within configured reopen window')
        }
      />
    );
  }

  function getRuleStatusChange() {
    return (
      <Metadata
        isInbound={false}
        prepositionalPhrases={getRuleStatusChangePrepositionalPhrase()}
        text={item.content.status === Conversation.Status.CLOSED ? t('Conversation closed') : t('Conversation opened')}
        timestamp={item.timestamp}
      />
    );
  }

  function getRuleStatusChangePrepositionalPhrase() {
    let ruleComplement = <RuleLink ruleId={item.initiator.id}>{t('rule')}</RuleLink>;

    return <PrepositionalPhrase complement={ruleComplement} key="by" preposition={t('by')} />;
  }
}

StatusChangeItem.propTypes = {
  item: PropTypes.instanceOf(ConversationItem).isRequired,
};

function mapStateToProps({ isFeatureEnabled }, { item }) {
  return {
    item,
  };
}

const StatusChangeItemContainer = connect(mapStateToProps, null)(StatusChangeItem);

StatusChangeItemContainer.propTypes = {
  item: PropTypes.instanceOf(ConversationItem).isRequired,
};

export default StatusChangeItemContainer;
